<template>
  <div
    ref="wrapper"
    class="nav-scroll-wrapper absolute left-full top-0 lg:left-0 lg:top-full w-full nav__tier-2 nav__content"
  >
    <nav class="absolute top-0 w-full bg-white lg:bg-transparent z-10">
      <nav-back-button :label="navBackButtonLabel" class="sticky top-0 z-10" />
      <ul
        ref="subNav"
        class="sub-nav relative flex list-none overflow-x-auto overflow-y-hidden pt-2 pb-1 px-2 gap-6 w-full max-w-full"
        role="menu"
      >
        <li
          v-for="item of filteredNavItemChildren"
          ref="categoryNavItems"
          :key="item.uid"
          class="text-nowrap"
          role="none"
        >
          <a
            ref="subNavLink"
            role="menuitem"
            :href="composeLink(item.url.url)"
            :target="setTarget(item.url.target)"
            class="category-nav nav__menu-item font-bold"
            :qaid="item.url.uid"
            @click.prevent="scrollToOnClick"
            @keydown.prevent.up="focusParentTier"
            @keydown.prevent.shift.enter="focusParentTier"
            @keydown.prevent.exact.enter="scrollToOnClick"
            @keydown.prevent.down="scrollToOnClick"
            @keydown.prevent.right="focusNextElement"
            @keydown.prevent.left="focusPreviousMenuItem"
          >
            <span v-html="item.url.linkName" />
          </a>
        </li>
      </ul>
    </nav>
    <div ref="scrollSections" class="nav-content w-full bg-white px-2" role="none">
      <div ref="links" class="w-full flex flex-wrap justify-center gap-3" role="none">
        <a
          v-for="link of filteredSecondaryLinks"
          ref="navItem"
          :key="link.uid"
          role="menuitem"
          class="nav__menu-item underline mx-2 py-2 text-xs hover:no-underline"
          :href="composeLink(link.url)"
          :target="setTarget(link.target)"
          :qaid="link.uid"
          @keydown.prevent.up="focusSubNav"
          @keydown.prevent.right="focusNextElement"
          @keydown.prevent.left="focusPreviousElement"
        >
          <span v-html="link.linkName" />
        </a>
      </div>
      <div
        v-for="(item, index) of filteredNavItemChildren"
        :id="item.uid"
        ref="categorySections"
        :key="item.uid"
        class="category-section w-full relative py-3 lg:py-4 px-0 md:container mx-auto mb-2"
        :class="{'border-t border-t-gray-10': index !== 0}"
        role="none"
      >
        <div
          v-if="hasSectionSecondaryLink(item, 0) || hasSectionSecondaryLink(item, 1)"
          class="flex justify-between gap-2 mb-2 lg:mb-4"
          role="menu"
        >
          <h2 v-if="hasSectionSecondaryLink(item, 0)" class="block w-1/2 text-sm" role="none">
            <a
              ref="navItem"
              class="nav__menu-item underline mb-1 lg:mb-0 inline-block hover:no-underline"
              role="menuitem"
              :href="composeLink(item.secondaryLinks[0].url)"
              :target="setTarget(item.secondaryLinks[0].target)"
              :qaid="item.secondaryLinks[0].uid"
              @keydown.prevent.down="focusNextSubNav"
              @keydown.prevent.up="focusSubNav"
              @keydown.prevent.right="focusNextElement"
              @keydown.prevent.left="focusPreviousElement"
            >
              <span v-html="item.secondaryLinks[0].linkName" />
              <pdl-icon name="chevron_right" class="ml-25" style="color: var(--nav-accent-color)" />
            </a>
          </h2>
          <a
            v-if="hasSectionSecondaryLink(item, 1)"
            ref="navItem"
            class="nav__menu-item w-1/2 underline mb-1 text-right lg:mb-0 text-xs lg:text-sm hover:no-underline"
            role="menuitem"
            :href="composeLink(item.secondaryLinks[1].url)"
            :target="setTarget(item.secondaryLinks[1].target)"
            :qaid="item.secondaryLinks[1].uid"
            @keydown.prevent.down="focusNextSubNav"
            @keydown.prevent.up="focusSubNav"
            @keydown.prevent.right="focusNextElement"
            @keydown.prevent.left="focusPreviousElement"
          >
            <span v-html="item.secondaryLinks[1].linkName" />
          </a>
        </div>
        <ul class="grid gap-4 md:grid-cols-2 lg:grid-cols-3 list-none" role="menu">
          <nav-bike-card
            v-for="card of filteredBikeCards(item)"
            :key="card.uid"
            ref="card"
            :up-function="focusSubNav"
            :down-function="focusNextSubNav"
            :left-function="focusPreviousElement"
            :right-function="focusNextElement"
            :nav-item-data="card"
          />
          <li
            v-for="card of filteredCategoryCardEditorialComponentData(item)"
            :key="card.uid"
            class="lg:-mb-10"
            role="none"
          >
            <NavCard
              :card="card"
              :up-function="focusSubNav"
              :down-function="focusNextSubNav"
              :left-function="focusPreviousElement"
              :right-function="focusNextElement"
            >
            </NavCard>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import NavBackButton from '@/navigation/NavBackButton.vue';
import NavBikeCard from '@/navigation/NavBikeCard.vue';
import {PdlIcon} from '@pedal/pdl-icon';
import NavCard from '@/navigation/components/NavCard.vue';
import links from '@/navigation/mixins/links.js';

export default {
  name: 'NavCategoryScroll',
  components: {
    NavBackButton,
    NavBikeCard,
    PdlIcon,
    NavCard,
  },
  mixins: [links],
  inject: ['isNavOpen', 'focusParentTier', 'focusPreviousMenuItem'],
  props: {
    navItemData: {
      type: Object,
      default: () => ({}),
    },
    navBackButtonLabel: {
      type: String,
      default: 'Back',
    },
    loadNavImages: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      lastKnownScrollPosition: 0,
      ticking: false,
      currentIndex: 0,
      isClicked: false,
      timeout: false,
      observer: null,
      isFocused: false,
      links: null,
      sectionLinks: [],
      navLinks: [],
      focusElement: undefined,
    };
  },
  computed: {
    supportsScrollend() {
      return 'onscrollend' in window;
    },
    navElements() {
      let els = [];
      this.$refs.wrapper.querySelectorAll('.nav__menu-item').forEach((el) => {
        els.push(el);
      });
      return els;
    },
    filteredNavItemChildren() {
      return this.navItemData.children.filter(
        (child) =>
          child.visible &&
          child?.url &&
          child?.url?.hasVisibleProducts !== false &&
          child?.url?.visible !== false &&
          child?.links?.length > 0
      );
    },
    filteredSecondaryLinks() {
      return (
        this.navItemData.secondaryLinks.filter(
          (link) => link?.hasVisibleProducts !== false && link?.visible !== false
        ) || []
      );
    },
  },
  mounted() {
    this.observeAriaExpanded();
    this.navElements.forEach((link) => {
      link.tabIndex = -1;
    });
    this.$refs.categorySections.forEach((section) => {
      this.sectionLinks.push(Array.from(section.querySelectorAll('[role="menuitem"]')));
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    observeAriaExpanded() {
      const target = this.$refs.wrapper.previousElementSibling;
      if (!target) return;

      const observerOptions = {
        attributeFilter: ['aria-expanded'],
        attributeOldValue: true,
      };

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          const expanded = mutation.target.getAttribute('aria-expanded');
          if (expanded === 'true') {
            this.setEventListeners([{oldValue: 'false'}]);
          } else {
            this.setEventListeners([{oldValue: 'true'}]);
          }
        });
      });

      this.observer.observe(target, observerOptions);

      if (target.getAttribute('aria-expanded') === 'true') {
        this.setEventListeners([{oldValue: 'false'}]);
      }
    },
    updateIsClicked() {
      this.isClicked = false;
      this.$refs.scrollSections.removeEventListener('scrollend', this.updateIsClicked);
    },
    scrollToTarget(el, behavior = 'smooth') {
      el.scrollIntoView({behavior, inline: 'center', block: 'start'});

      if (this.supportsScrollend) {
        this.$refs.scrollSections.addEventListener('scrollend', this.updateIsClicked);
      } else {
        setTimeout(() => {
          this.isClicked = false;
        }, 500);
      }
    },
    updateActiveSubNavState(i) {
      this.$refs.categoryNavItems[this.currentIndex]?.classList.remove('active');
      this.currentIndex = i;

      if (!this.$refs.subNav || !this.$refs.categoryNavItems) return;
      this.$refs.subNav.style.setProperty('--indicator-opacity', '1');
      this.$refs.subNav.style.setProperty('--indicator-width', `${this.$refs.categoryNavItems[i].offsetWidth}px`);
      this.$refs.subNav.style.setProperty('--indicator-offset', `${this.$refs.categoryNavItems[i].offsetLeft}px`);

      this.$refs.categoryNavItems[i].classList.add('active');

      this.scrollToTarget(this.$refs.categoryNavItems[i]);
    },
    checkNavScrollPosition() {
      let activeZoneSize = 0;

      this.$refs.categorySections.forEach((el, i) => {
        if (
          this.$refs.scrollSections.scrollTop >= activeZoneSize - 2 &&
          this.$refs.scrollSections.scrollTop <= activeZoneSize + el.offsetHeight &&
          i !== this.currentIndex &&
          !this.isClicked
        ) {
          this.updateActiveSubNavState(i);
        }

        activeZoneSize += el.offsetHeight;
      });
    },
    scrollToOnClick(e) {
      e.target.tabIndex = -1;

      const targetIndex = this.$refs.categoryNavItems.indexOf(e.target.closest('li'));
      this.isClicked = true;
      this.isFocused = true;
      this.updateActiveSubNavState(targetIndex);
      this.scrollToTarget(this.$refs.categorySections[targetIndex]);
      this.sectionLinks[targetIndex][0].tabIndex = 0;
      this.sectionLinks[targetIndex][0].focus({preventScroll: true});
    },

    rAFDebuffScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.checkNavScrollPosition();
          this.ticking = false;
        });

        this.ticking = true;
      }
    },
    handleResize() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.updateActiveSubNavState(this.currentIndex), 250);
    },
    setEventListeners(mutation) {
      if (mutation[0].oldValue === 'false') {
        // account for scroll-bar collapse
        setTimeout(() => {
          this.updateActiveSubNavState(0);
        }, 50);
        this.$refs.scrollSections.scrollTop = 0;
        this.$refs.scrollSections.addEventListener('scroll', this.rAFDebuffScroll, {passive: true});
        window.addEventListener('resize', this.handleResize);
        this.loadNavImages(this.$refs.wrapper);
      } else {
        this.isFocused = false;
        this.$refs.scrollSections.scrollTop = 0;
        this.updateActiveSubNavState(0);
        window.removeEventListener('resize', this.handleResize);
        this.$refs.scrollSections.removeEventListener('scroll', this.rAFDebuffScroll, {passive: true});
      }
    },
    focusSubNav(e) {
      e.target.tabIndex = -1;
      this.$refs.subNavLink[this.currentIndex].tabIndex = 0;
      this.$refs.subNavLink[this.currentIndex].focus();
    },
    focusNextSubNav(e) {
      e.target.tabIndex = -1;
      const targetIndex = this.currentIndex < this.$refs.subNavLink.length - 1 ? this.currentIndex + 1 : 0;
      this.$refs.subNavLink[targetIndex].tabIndex = 0;
      this.$refs.subNavLink[targetIndex].focus();
    },
    focusNextElement(e) {
      const elIndex = this.navElements.indexOf(e.target);
      e.target.tabIndex = -1;
      const targetIndex = elIndex < this.navElements.length - 1 ? elIndex + 1 : 0;
      this.navElements[targetIndex].tabIndex = 0;
      this.navElements[targetIndex].focus();
    },
    focusPreviousElement(e) {
      const elIndex = this.navElements.indexOf(e.target);
      e.target.tabIndex = -1;
      const targetIndex = elIndex > 0 ? elIndex - 1 : this.navElements.length - 1;
      this.navElements[targetIndex].tabIndex = 0;
      this.navElements[targetIndex].focus();
    },
    hasSectionSecondaryLink(item, i) {
      return (
        item?.secondaryLinks.length > 0 &&
        !!item?.secondaryLinks[i]?.hasVisibleProducts !== false &&
        item?.secondaryLinks[i]?.visible !== false
      );
    },
    filteredCategoryCardEditorialComponentData(item) {
      return item.categoryCardEditorialComponentData?.filter(
        (card) => card.ctaLinks[0]?.hasVisibleProducts !== false && card.ctaLinks[0]?.visible !== false
      );
    },
    filteredBikeCards(item) {
      return item.links.filter((link) => link?.hasVisibleProducts !== false && link?.visible !== false);
    },
  },
};
</script>

<style lang="scss" scoped>
.nav-scroll-wrapper {
  --nav-focus-offset: 0;

  visibility: hidden;
  pointer-events: none;
  height: 100dvh;

  @media (screen(lg)) {
    height: calc(85vh - var(--nav-tier-1-height, 0px) - var(--header-height));
  }

  [aria-expanded='true'] ~ & {
    visibility: visible;
    pointer-events: all;
  }
}

.sub-nav {
  justify-content: safe center;
  -webkit-overflow-scrolling: touch;

  &::before {
    content: '';
    position: absolute;
    background: var(--nav-accent-color);
    height: 2px;
    left: 0;
    bottom: 4px;
    width: var(--indicator-width);
    opacity: var(--indicator-opacity, 0);
    transform: translateX(var(--indicator-offset));
    // prettier-ignore
    transition: transform var(--nav-reduced-motion, 0.25s) ease-in-out, width var(--nav-reduced-motion, 0.25s) ease-in-out, opacity var(--nav-reduced-motion, 0.125s) ease-in-out var(--nav-reduced-motion, 0.25s);
  }
}

.nav-content {
  position: absolute;
  top: 101px;
  width: 100%;
  height: calc(100dvh - 101px - var(--header-height, 0px));
  overflow: auto;
  display: block;
  min-height: 0;

  @media (screen(lg)) {
    top: 64px;
    height: calc(85vh - var(--nav-tier-1-height, 0px) - var(--header-height) - 64px);
  }
}

.category-section {
  &:last-child {
    min-height: calc(100vh - var(--nav-tier-1-height, 0px) - var(--header-height) - 64px);

    @media (screen(lg)) {
      min-height: calc(85vh - var(--nav-tier-1-height, 0px) - var(--header-height) - 64px);
    }
  }
}
</style>
